import axios from "axios";
import { PORT } from "./config";
import { handleLogErrorToSlack } from "./Helper";

const defaultOptions = {
  baseURL: PORT,
  timeout: 180000,
  headers: {
    "Content-Type": "application/json",
  },
  validateStatus: function (status) {
    return status < 400; // Resolve only if the status code is less than 500
  },
};

const axiosInstance = axios.create(defaultOptions);

axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  config.headers.Authorization = token
    ? `Token ${window.localStorage.token}`
    : "";
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (!error.config.url.includes("error-log-slack")) {
      handleLogErrorToSlack(error, false);
    }
    return Promise.reject(error);
  }
);

export default class coreAPI {
  constructor() {
    this._apiCore = axiosInstance;
  }

  // Add new api calls here, group by Model

  // Shared

  tokenCheck() {
    return this._apiCore.get(`/tokenCheck`);
  }

  getFeedbackByTeacherID(id, page) {
    return this._apiCore.get(`/v2/feedback?teacher_id=${id}&limit=5&page=${page}`);
  }

  getFeedbackBadgeByTeacherID(id) {
    return this._apiCore.get(`/v2/feedback/count-badge?teacher_id=${id}`);
  }

  permissionCheck(permission) {
    return this._apiCore.post(`/permissionCheck`, permission);
  }

  changePassword(userData) {
    return this._apiCore.put("/changepassword", userData);
  }

  changeSecretQAndA(userData) {
    return this._apiCore.put("/changesecretqanda", userData);
  }

  confirmPassword(userData) {
    return this._apiCore.post("/confirmPassword", userData);
  }

  // Teachers

  login(userCredentials) {
    return this._apiCore.post(`/login`, userCredentials);
  }

  loginGoogle(googleToken) {
    return this._apiCore.post("/user/loginGoogle", { token: googleToken });
  }

  setSecondRegister(secondRegData) {
    return this._apiCore.put(`/user/secret`, secondRegData);
  }

  setCustomerSecondRegister(secondRegData) {
    return this._apiCore.put(`/customer/secondaryregister`, secondRegData);
  }

  setPersonnelSecondRegister(secondRegData) {
    return this._apiCore.put(`/personnel/secondaryregister`, secondRegData);
  }

  getUserSafeData() {
    const { userID } = window.localStorage;
    return this._apiCore.get(`/user/getsafedata/${userID}`);
  }

  getTeacherDetailByID(ID) {
    return this._apiCore.get(`/getTeacherDetailByID/${ID}`);
  }

  getUserSensitiveData(userID = window.localStorage.userID) {
    // const { userID } = window.localStorage
    return this._apiCore.get(`/user/getusersensitivedata/${userID}`);
  }

  deleteTeacherAccount(userID = window.localStorage.userID) {
    return this._apiCore.delete(`/user/${userID}`);
  }

  allTeachers() {
    return this._apiCore.get(`/allteachers`);
  }

  allUsers(page, limit, keyword, filter = {}) {
    const keywordParam = keyword ? `&keyword=${keyword}` : "";
    const query = {
      page,
      limit,
      ...filter,
    };
    return this._apiCore.get(`/users`, {
      params: { ...query },
    });
  }

  listInvoicesInAdmin(page, limit, filter = {}) {
    let filterParam = ""
    filterParam += filter.status ? `&filter[status]=${filter.status}` : ""
    filterParam += filter.approver ? `&filter[approver]=${filter.approver}` : ""
    filterParam += filter.campus ? `&filter[campus]=${filter.campus}` : ""
    filterParam += filter.period_date ? `&filter[period_date]=${filter.period_date}` : ""
    filterParam += filter.duo_date ? `&filter[duo_date]=${filter.duo_date}` : ""
    filterParam += filter.customer_id ? `&filter[customer_id]=${filter.customer_id}` : ""
    return this._apiCore.get(`/listInvoicesInAdmin?page=${page}&limit=${limit}&${filterParam}`);
  }

  exportTeacherWithFilter(filter = {}) {
    const query = {
      ...filter,
    };
    return this._apiCore.get(`/exportTeachers`, {
      params: { ...query },
      responseType: "blob",
    });
  }

  getAllAvailability(page, limit, filter) {
    let filterParam = "";
    filterParam += filter.name ? `&filter[name]=${filter.name}` : "";
    filterParam += filter.email ? `&filter[email]=${filter.email}` : "";
    filterParam += filter.state ? `&filter[state]=${filter.state}` : "";
    filterParam += filter.availability_date
      ? `&filter[availability_date]=${filter.availability_date}`
      : "";
    filter.can_deliver.map(
      (cls) => (filterParam += `&filter[can_deliver]=${cls.value}`)
    );
    // console.log(`/getAllAvailability?page=${page}&limit=${limit}${filterParam}`)
    return this._apiCore.get(
      `/getAllAvailability?page=${page}&limit=${limit}${filterParam}`
    );
  }

  fetchInstitutionBookingsWithPagination(page, limit, institution_id, filter) {
    let filterParam = "";
    filterParam += filter?.mode ? `&filter[mode]=${filter.mode}` : "";
    filterParam += filter?.campus_id
      ? `&filter[campus_id]=${filter.campus_id}`
      : "";
    filterParam += filter?.date_type
      ? `&filter[date_type]=${filter.date_type}`
      : "";
    filterParam += filter?.status ? `&filter[status]=${filter.status}` : "";
    filterParam += filter?.user_id ? `&filter[user_id]=${filter.user_id}` : "";
    filterParam += filter?.keyword ? `&filter[keyword]=${filter.keyword}` : "";
    return this._apiCore.get(
      `/fetchInstitutionBookingsWithPagination?page=${page}&limit=${limit}&institution_id=${institution_id}${filterParam}`
    );
  }

  fetchBookingsByTeacherIDWithPagination(
    page,
    limit,
    teacher_id,
    sortType,
    filter
  ) {
    let sortTypeBookings = sortType && sortType === "sort-date-desc" ? -1 : 1;
    return this._apiCore.get(
      `/fetchBookingsByTeacherIDWithPagination?page=${page}&limit=${limit}&teacher_id=${teacher_id}&sortType=${sortTypeBookings}`
    );
  }

  allTeachersForAllocate() {
    return this._apiCore.get("/getAllUsersForAllocatePage");
  }

  allInstUsers() {
    return this._apiCore.get(`/getAllInstUsers`);
  }

  allInstUsersForTeacher() {
    return this._apiCore.get(`/getAllInstUsersForTeacher`);
  }

  allInstUsersForAllocate() {
    return this._apiCore.get(`/getAllInstUsersForAllocate`);
  }

  updateUser(userData) {
    return this._apiCore.put(`/updateUser`, userData);
  }

  verifyBankDetails() {
    const { userID } = window.localStorage;
    return this._apiCore.get(`/banking/${userID}`);
  }

  getSafeBankData() {
    const { userID } = window.localStorage;
    return this._apiCore.get(`/banking/safe/${userID}`);
  }

  getFullBankData(userID) {
    return this._apiCore.get(`/banking/admin/${userID}`);
  }

  updateBankData(userData) {
    const { userID } = window.localStorage;
    return this._apiCore.put(`/banking/update/`, { id: userID, ...userData });
  }

  deleteBankData() {
    const { userID } = window.localStorage;
    return this._apiCore.delete(`/banking/${userID}`);
  }

  searchTeacher(query) {
    return this._apiCore.get("/searchTeacher", { params: query });
  }

  updateSearchStatus(userID, is_searchable) {
    return this._apiCore.put("/updateSearchStatus", {
      id: userID,
      is_searchable: is_searchable,
    });
  }

  updateTeacherLevel(userID, level) {
    // console.log(userID, level)
    return this._apiCore.patch("/updateUserLevel", {
      level: level,
      teacher_id: userID,
    });
  }

  // Institution

  registerCustomerGoogle(email) {
    return this._apiCore.post("/customer/registergoogle", email);
  }

  loginCustomerPersonnelGoogle(googleToken) {
    return this._apiCore.post("/institution/logingoogle", {
      token: googleToken,
    });
  }

  institutionLogin(userData) {
    return this._apiCore.post("/institution/login", userData);
  }

  getInstitutionData(instData) {
    return this._apiCore.get(
      `/getInstitutionByInstitutionID?institution_id=${instData.id}`
    );
  }

  getInstitutionDataById(instId) {
    return this._apiCore.get(
      `/getInstitutionByInstitutionID?institution_id=${instId}`
    );
  }

  getMultipleCampusById(campuses) {
    return this._apiCore.get(`/getMultiCampusesByIDs`, {
      params: { multiIDs: campuses },
    });
  }

  getMultipleInstitutionById(instIDs) {
    return this._apiCore.get(`/getMultipleInstitutionById`, {
      params: { instIDs: instIDs },
    });
  }

  getPersonnelByInstId(inst_id) {
    return this._apiCore.get(`/getPersonnelsByInstID`, {
      params: { inst_id: inst_id },
    });
  }


  getClassById(class_id) {
    return this._apiCore.get(`/getClassByID`, { params: { _id: class_id } });
  }

  getClassesByCampuses(campuses) {
    return this._apiCore.get(`/getClassesByMultiCampusID`, {
      params: { campusIDs: campuses },
    });
  }

  getCustomerSafeDataV2() {
    const { userID } = window.localStorage;
    return this._apiCore.get(`/V2/customer/getsafedata/${userID}`);
  }

  // get all customers for admin - lieh
  allCustomers() {
    return this._apiCore.get(`/customers`);
  }

  allArchivedCollegs() {
    return this._apiCore.get("/getListArchivedAccount");
  }

  allCustomersForTeacher() {
    return this._apiCore.get(`/teacher/cutomers`);
  }

  getUserLog(email, type, page, limit) {
    const filter =
      (email ? "&email=" + email : "") + (type ? "&type=" + type : "");
    // console.log(`/userLogs?page=${page || 1}&limit=${limit || 20}${filter}`)
    return this._apiCore.get(
      `/userLogs?page=${page || 1}&limit=${limit || 20}${filter}`
    );
  }

  getAllTypeNameAndActorEmail() {
    return this._apiCore.get(`/getAllTypeNameAndActorEmail`);
  }

  getLogDetail(logId) {
    return this._apiCore.get(`/getLogDetail/${logId}`);
  }

  updateCustomer(customerData) {
    return this._apiCore.put("/updateCustomer", customerData);
  }

  updateFinanceSettingCustomer(customer_id, customer_data) {
    return this._apiCore.put("/updateFinanceSettingCustomer", {
      id: customer_id,
      customer_data: customer_data
    });
  }

  financeSettingForCustomer(institution_id) {
    return this._apiCore.get(`/v2/customer/finance-settings/${institution_id}`);
  }

  // update personnel details for adding profile photo - lieh
  updatePersonnelDetails(personnelData) {
    return this._apiCore.put("/updatePersonnelDetails", personnelData);
  }

  getAllInst() {
    return this._apiCore.get(`/getAllInst`);
  }

  getAllInstWithCache() {
    return this._apiCore.get(`/getAllInstForAllocate`);
  }

  getAllCampus() {
    return this._apiCore.get(`/getAllCampus`);
  }

  getAllCampusWithCache() {
    return this._apiCore.get(`/getAllCampusForAllocate`);
  }

  getAllClasses() {
    return this._apiCore.get(`/getAllClasses`);
  }

  getListBooking(query) {
    return this._apiCore.get("/getListBooking", {
      params: { ...query },
    });
  }

  getBookingsByTeacherID(id, ninResponses) {
    if (typeof ninResponses === "undefined" || ninResponses === null) {
      ninResponses = [""];
    }

    var arrayParams = ninResponses
      .map(function (el, idx) {
        return "not_in_responses[" + idx + "]=" + el;
      })
      .join("&");

    return this._apiCore.get(
      `/getBookingsByTeacherID?teacher_id=${id}&${arrayParams}`
    );
  }
  getBookingByBookingID(id) {
    // console.log(id);
    return this._apiCore.get("/getBookingByBookingID?booking_id=" + id);
  }

  getAllBookingsByTeacherID(id, ninResponses) {
    if (typeof ninResponses === "undefined" || ninResponses === null) {
      ninResponses = [""];
    }

    var arrayParams = ninResponses
      .map(function (el, idx) {
        return "not_in_responses[" + idx + "]=" + el;
      })
      .join("&");

    return this._apiCore.get(
      `/getAllBookingsByTeacherID?teacher_id=${id}&${arrayParams}`
    );
  }

  getAllBookingsByTeacherIDPagination(id, ninResponses, start_date, end_date) {
    if (typeof ninResponses === "undefined" || ninResponses === null) {
      ninResponses = [""];
    }

    var arrayParams = ninResponses
      .map(function (el, idx) {
        return "not_in_responses[" + idx + "]=" + el;
      })
      .join("&");

    return this._apiCore.get(
      `/getAllBookingsByTeacherIDPagination?teacher_id=${id}&${arrayParams}&start_date=${start_date}&end_date=${end_date}`
    );
  }

  getCampusBookingsByInstitutionID(institution_id, future) {
    return this._apiCore.get("/getCampusBookingsByInstitutionID", {
      params: { institution_id, future },
    });
  }

  getCampusBookingsByInstitutionIDPagination(
    institution_id,
    start_date,
    end_date,
    future
  ) {
    return this._apiCore.get("/getCampusBookingsByInstitutionIDPagination", {
      params: { institution_id, start_date, end_date, future },
    });
  }

  getCampusBookingsByInstitutionIDPaginationV2(
    institution_id,
    start_date,
    end_date,
    future
  ) {
    return this._apiCore.get("/V2/bookings/getCampusBookingsByInstitutionIDPagination", {
      params: { institution_id, start_date, end_date, future },
    });
  }

  exportBookingsForCustomer(customer_id, start_date, end_date, campuses) {
    var arrayParams = campuses
      .map(function (campus, idx) {
        return "campus_ids[" + idx + "]=" + campus;
      })
      .join("&");

    return this._apiCore.get(
      `/exportCustomerBooking/${customer_id}?start_date=${start_date}&end_date=${end_date}&${arrayParams}`,
      {
        responseType: "blob",
      }
    );
  }

  exportBookingsForAdmin(customer_id, start_date, end_date, campuses) {
    var arrayParams = campuses
      .map(function (campus, idx) {
        return "campus_ids[" + idx + "]=" + campus;
      })
      .join("&");

    return this._apiCore.get(
      `/exportAdminBooking?start_date=${start_date}&end_date=${end_date}&customer_ids[0]=${customer_id}&${arrayParams}`,
      {
        responseType: "blob",
      }
    );
  }

  exportBookingForXero(start_date, end_date, campuses) {
    var arrayParams = campuses
      .map(function (campus, idx) {
        return "campus_ids[" + idx + "]=" + campus;
      })
      .join("&");

    return this._apiCore.get(
      `/exportBookingForXero?${arrayParams}&start_date=${start_date}&end_date=${end_date}`,
      {
        responseType: "blob",
      }
    );
  }

  exportTeacherBookingsByAdmin(teacherIDs, start_date, end_date) {
    // var arrayParams = teacherIDs.map(function (id, idx) {
    //   return "teacher_ids[" + idx + "]=" + id;
    // }).join("&");

    return this._apiCore.post(
      `/exportTeacherBookingsByAdmin?start_date=${start_date}&end_date=${end_date}`,
      {
        teacher_ids: teacherIDs,
      },
      {
        responseType: "blob",
      }
    );
  }

  addTeacherToXero(teacherIDs) {
    return this._apiCore.post("/importTeacherForXero", {
      teacher_ids: teacherIDs,
    });
  }

  exportTeacherBookingsXeroByAdmin(
    teacherIDs,
    start_date,
    end_date,
    start_date_b,
    end_date_b
  ) {
    return this._apiCore.post(
      `/getExportBookingForXero`,
      {
        teacher_ids: teacherIDs,
        start_date: start_date,
        end_date: end_date,
        start_date_b: start_date_b,
        end_date_b: end_date_b,
      },
      {
        responseType: "blob",
      }
    );
  }

  connectXeroPayroll() {
    return this._apiCore.get("/connectXeroPayroll");
  }

  importXeroPayRoll(
    teacher_ids,
    start_date,
    end_date,
    start_date_b,
    end_date_b
  ) {
    return this._apiCore.post("/importXeroPayRoll", {
      teacher_ids,
      start_date,
      end_date,
      start_date_b,
      end_date_b,
    });
  }

  shareVideo(to, title, videoURL) {
    return this._apiCore.post("/shareVideo", { to, title, videoURL });
  }

  createCampus(campusData) {
    return this._apiCore.post("/createCampus", campusData);
  }

  createCampusByPersonnel(campusData) {
    return this._apiCore.post("/createCampusByPersonnel", campusData);
  }

  updateCampus(campusData) {
    return this._apiCore.post("/updateCampus", campusData);
  }

  deleteCampus(campusData) {
    return this._apiCore.delete("/campus/delete", { data: campusData });
  }

  createPersonnel(personnelData) {
    return this._apiCore.post("/personnel/create", personnelData);
  }

  updatePersonnel(personnel) {
    return this._apiCore.post("/customer/update/personnel", {
      personnel: personnel.personnel,
      is_new_personnel: personnel.is_new_personnel
    });
  }

  deletePersonnel(personnelData) {
    return this._apiCore.delete("/personnel/delete", {
      data: { p_id: personnelData._id },
    });
  }

  createClass(classData) {
    return this._apiCore.post("/createClass", classData);
  }

  updateClass(classData) {
    return this._apiCore.post("/updateClass", classData);
  }

  // updateClassReferenceName(classData) {
  //   return this._apiCore.post("/updateClassReferenceName", classData)
  // }

  updateBookingLevel(id, level, levelID = "") {
    return this._apiCore.patch("/updateBookingLevel/" + id, { level: level, class_level_id: levelID });
  }

  collegeUpdateBookingLevel(id, level, levelID) {
    return this._apiCore.patch("/collegeUpdateBookingLevel/", {
      id: id,
      level: level,
      class_level_id: levelID
    });
  }

  deleteClass(class_id) {
    return this._apiCore.delete("/class/delete", { data: { _id: class_id } });
  }

  instProgressEmail(email) {
    return this._apiCore.post("/sendInstProgressEmail", {
      inst_id: email.inst_id,
      email_address: email.email_address,
      etype: email.etype,
    });
  }

  addMultipleDayBooking(new_booking, to) {
    return this._apiCore.post("/addMultipleDayBooking", {
      new_booking: new_booking,
      to: to,
    });
  }

  markUnAvailableForBooking(booking_id, updated_splits, dates, split_index) {
    return this._apiCore.post("/markUnAvailableForBooking", {
      booking_id,
      updated_splits,
      dates,
      split_index,
    });
  }

  updateInstLogo(inst, sfx) {
    let inst_id = sfx.inst_id;
    let suffix = sfx.suffix;
    return this._apiCore.post("/updateLogoUrl", { inst_id, suffix });
  }
  updateInstTagline(inst_id, tagline) {
    return this._apiCore.put("/updateInstTagline", { inst_id, tagline });
  }

  updateInstWebsite(inst_id, url) {
    return this._apiCore.put("/updateInstWebsite", { inst_id, url });
  }

  updateInstCanDeliver(inst_id, can_deliver) {
    return this._apiCore.put("/updateInstCanDeliver", { inst_id, can_deliver });
  }

  updateInstType(inst_id, type) {
    return this._apiCore.patch("/setInstitutionType", {
      inst_id: inst_id,
      inst_type: type,
    });
  }

  sizeOf(key) {
    return this._apiCore.put("/sizeof", { key: key });
  }

  getAllTeachersBrief() {
    return this._apiCore.get("/AllTeacherBrief");
  }

  getAllBookings() {
    return this._apiCore.get("/getAllBookingsMongoDB");
  }

  // booking_id here is actually the correct ._id field in booking - lieh
  respondToBooking(
    teacher_id,
    booking_id,
    teacher_response,
    teacher_cancel_reason
  ) {
    return this._apiCore.put("/teacherRespond", {
      id: teacher_id,
      booking_id,
      teacher_response,
      teacher_cancel_reason,
    });
  }

  respondToBookingMultipleDate(
    teacher_id,
    booking_id,
    teacher_response,
    split_index,
    teacher_cancel_reason
  ) {
    return this._apiCore.put("/teacherRespondMultipleDate", {
      id: teacher_id,
      booking_id,
      teacher_response,
      split_index,
      teacher_cancel_reason,
    });
  }

  requestDayOff(booking_id, split_index, date_index) {
    return this._apiCore.patch("/markADayOfTheBookingIsDayOff", {
      booking_id,
      split_index,
      date_index,
    });
  }

  deallocateSingle(booking_id) {
    return this._apiCore.put("/deallocateSingle", { booking_id });
  }

  deallocateSplit(booking_id, split_index) {
    return this._apiCore.put("/deallocateSplit", { booking_id, split_index });
  }

  cancelBooking(booking_id, wages_percent, admin_fee) {
    const { userType } = window.localStorage;
    let userEmail = "";
    if (userType === "institutionLogin") {
      const user = JSON.parse(
        JSON.parse(window.localStorage["persist:root"]).college
      )?.user;
      userEmail = user?.email || user?.personnel_email;
    }
    console.log("User email canceling: ", userEmail);
    // console.log("wage percent", wages_percent)
    // console.log("admin fee", admin_fee)
    return this._apiCore.put("/cancelBooking", {
      booking_id,
      user_email: userEmail,
      wages_percent: wages_percent,
      admin_fee: admin_fee,
    });
  }

  getInvoiceDataForCollege(invoice_id) {
    return this._apiCore.get(
      `/getInvoiceDataForCollege/${invoice_id}`
    );
  }

  getListInvoices(page, limit, customer_id, filter = {}, is_personnel, institution_id) {
    let filterParam = ""
    filterParam += filter.status ? `&filter[status]=${filter.status}` : ""
    filterParam += filter.approver ? `&filter[approver]=${filter.approver}` : ""
    filterParam += filter.campus ? `&filter[campus]=${filter.campus}` : ""
    filterParam += filter.period_date ? `&filter[period_date]=${filter.period_date}` : ""
    filterParam += filter.duo_date ? `&filter[duo_date]=${filter.duo_date}` : ""
    filterParam += filter.today ? `&filter[today]=${filter.today}` : ""
    return this._apiCore.get(`/v2/customer/invoices?page=${page}&limit=${limit}&is_personnel=${is_personnel}&customer_id=${customer_id}&institution_id=${institution_id}&${filterParam}`);
  }

  getInvoiceByID(id) {
    return this._apiCore.get(`/getInvoiceByID/${id}`);
  }

  approveOrRejectInvoiceItemsInAdmin(id, content, totalPrice) {
    return this._apiCore.put(`/v2/invoice/approve-reject-invoice/${id}`, {
      content: content,
      total_price: totalPrice
    });
  }

  updateInvoiceForCustomer(invoice_id, invoice_data, mark_as_done = false) {
    return this._apiCore.put(`/v2/invoice/${invoice_id}`, {
      invoiceData: invoice_data,
      mark_as_done: mark_as_done
    });
  }

  createInvoiceForAdmin(customer_id, campus_ids, start_date, end_date) {
    return this._apiCore.post("/createInvoiceForAdmin", {
      start_date,
      end_date,
      customer_id,
      campus_ids
    })
  }

  markAsPaidInvoices(invoice_ids) {
    return this._apiCore.post("/markAsPaidInvoices", {
      invoice_ids
    });
  }

  splitBookingForCancelSingleDate(booking_id, date_index) {
    return this._apiCore.put("/splitBookingForCancelSingleDate", {
      booking_id,
      date_index,
    });
  }

  cancelMultiDateBooking(
    booking_id,
    split_index,
    inst_cancelled_reason,
    wages_percent,
    admin_fee
  ) {
    const { userType } = window.localStorage;
    let userEmail = "";
    if (userType === "institutionLogin") {
      const user = JSON.parse(
        JSON.parse(window.localStorage["persist:root"]).college
      )?.user;
      userEmail = user?.email || user?.personnel_email;
    }
    // console.log("wage percent", wages_percent)
    // console.log("admin fee", admin_fee)
    return this._apiCore.put("/cancelMultiDateBooking", {
      booking_id,
      split_index,
      user_email: userEmail,
      inst_cancelled_reason: inst_cancelled_reason,
      wages_percent: wages_percent,
      admin_fee: admin_fee,
    });
  }

  addRemoveFavouriteTeacher(_id, fav_teacher_list) {
    return this._apiCore.put("/addRemoveFavouriteTeacher", {
      _id,
      fav_teacher_list,
    });
  }

  blockUnblockTeacher(_id, block_list) {
    return this._apiCore.put("/blockUnblockTeacher", { _id, block_list });
  }

  // job board
  createListing(listing_data) {
    return this._apiCore.post("/createListing", listing_data);
  }

  deleteListing(listing_id) {
    return this._apiCore.delete("/deleteListing", {
      data: { _id: listing_id },
    });
  }

  updateListing(listing_data) {
    return this._apiCore.post("/updateListing", listing_data);
  }

  updateListingStatus(listing_id, status) {
    return this._apiCore.put("/updateListingStatus", {
      _id: listing_id,
      status: status,
    });
  }

  getAllListings() {
    return this._apiCore.get("/getallListings");
  }

  getPublishedListing() {
    return this._apiCore.get("/getPublishedListings");
  }

  getListingById(listing_id) {
    return this._apiCore.get(`/getListingById?id=${listing_id}`);
  }

  getListingByInst(inst_id) {
    return this._apiCore.get(`/GetListingsViaInst?inst_id=${inst_id}`);
  }

  applyForListing(listing_id, teacher_id, inst_name, publisher_id) {
    return this._apiCore.post("/ApplyForListing", {
      listing_id,
      teacher_id,
      inst_name,
      publisher_id,
    });
  }

  cancelApplication(listing_id, teacher_id) {
    return this._apiCore.post("/CancelApplication", { listing_id, teacher_id });
  }

  inviteToListing(listing_id, teacher_id, inst_name, publisher_id) {
    return this._apiCore.post("/InviteToListing", {
      listing_id,
      teacher_id,
      inst_name,
      publisher_id,
    });
  }

  cancelInvitation(listing_id, teacher_id) {
    return this._apiCore.post("/CancelInvitation", { listing_id, teacher_id });
  }

  inviteToInterview(listing_id, teacher_id) {
    return this._apiCore.post("/InviteToInterview", { listing_id, teacher_id });
  }

  inviteToInterviewWithScheduler(
    listing_id,
    teacher_id,
    interviewScheduleInfo
  ) {
    return this._apiCore.post("/InviteToInterviewWithScheduler", {
      listing_id,
      teacher_id,
      interviewScheduleInfo,
    });
  }

  cancelInterview(listing_id, teacher_id) {
    return this._apiCore.post("/CancelInterview", { listing_id, teacher_id });
  }

  offerJob(listing_id, teacher_id) {
    return this._apiCore.post("/OfferJob", { listing_id, teacher_id });
  }

  cancelOffer(listing_id, teacher_id) {
    return this._apiCore.post("/CancelOffer", { listing_id, teacher_id });
  }

  saveAsFavourite(listing_id, teacher_id) {
    return this._apiCore.post("/SaveAsFavourite", { listing_id, teacher_id });
  }

  removeFromFavourite(listing_id, teacher_id) {
    return this._apiCore.post("/RemoveFromFavourite", {
      listing_id,
      teacher_id,
    });
  }

  getMyApplications(teacher_id) {
    return this._apiCore.get(`/GetMyApplications?teacher_id=${teacher_id}`);
  }

  getViewCalendarApplicationsV2(teacher_id) {
    return this._apiCore.get(
      `/v2/requestViewCalendar/teacher-application-v2?teacher_id=${teacher_id}`
    );
  }

  selectInterviewTime(listing_id, interview_time_id) {
    return this._apiCore.post("/SelectInterviewTime", {
      listing_id,
      interview_time_id,
    });
  }

  getInterviewJobList(teacher_id) {
    return this._apiCore.get(`/GetMyApplications?teacher_id=${teacher_id}`);
  }

  getMyInvitations(teacher_id) {
    return this._apiCore.get(`/GetMyInvitations?teacher_id=${teacher_id}`);
  }

  getMyInterviews(teacher_id) {
    return this._apiCore.get(`/GetMyInterviews?teacher_id=${teacher_id}`);
  }

  getMyOffers(teacher_id) {
    return this._apiCore.get(`/GetMyOffers?teacher_id=${teacher_id}`);
  }

  getMyFavourite(teacher_id) {
    return this._apiCore.get(`/GetMyFavourite?teacher_id=${teacher_id}`);
  }

  getApplicants(listing_id) {
    return this._apiCore.get(`/GetApplicants?listing_id=${listing_id}`);
  }

  getInvitees(listing_id) {
    return this._apiCore.get(`/GetInvitees?listing_id=${listing_id}`);
  }

  getInterviewees(listing_id) {
    return this._apiCore.get(`/GetInterviewees?listing_id=${listing_id}`);
  }

  getSelected(listing_id) {
    return this._apiCore.get(`/GetSelected?listing_id=${listing_id}`);
  }

  getMyHires(inst_id) {
    return this._apiCore.get(`/GetMyHires?inst_id=${inst_id}`);
  }

  assignTeacherToBooking(url, data) {
    return this._apiCore.post(url, data);
  }

  sendLessonMaterial(
    type,
    customer_id,
    subject,
    content,
    attachments,
    booking_id,
    splits_index,
    level,
    institution_id,
    booking_items
  ) {
    return this._apiCore.post("/sendLessionMaterial", {
      type: type,
      customer_id: customer_id,
      booking_id: booking_id,
      splits_index: splits_index,
      subject: subject,
      content: content,
      attachments: attachments,
      level: level,
      institution_id: institution_id,
      booking_items: booking_items,
    });
  }

  addMessageToMaterialConversations(
    sender_id,
    receiver_id,
    receiver_account_type,
    subject,
    content,
    attachments,
    booking_id,
    material_id
  ) {
    return this._apiCore.post("/addMessageToMaterialConversations", {
      sender_id: sender_id,
      receiver_id: receiver_id,
      receiver_account_type: receiver_account_type,
      subject: subject,
      content: content,
      attachments: attachments,
      booking_id: booking_id,
      material_id: material_id,
    });
  }

  getNumberOfUnreadMaterialMessage() {
    return this._apiCore.get("/getNumberOfUnreadMaterialMessage");
  }

  markAConversationAsRead(conversation_id) {
    return this._apiCore.patch("/markAConversationAsRead", {
      conversation_id: conversation_id,
    });
  }

  setInstitutionGroup(inst_id, group) {
    return this._apiCore.patch("/setInstitutionGroup", {
      inst_id: inst_id,
      group_type: group,
    });
  }

  getTeacherNotes(teacherID) {
    return this._apiCore.get(`/userNotes/${teacherID}`);
  }

  getTeacherNotesV2(teacherID) {
    return this._apiCore.get(`V2/userNote/${teacherID}`);
  }

  createTeacherNote(teacherID, teacherEmail, firstName, lastName, note, additional_data) {
    return this._apiCore.post(`/v2/userNote/${teacherID}`, {
      ...note,
      teacher_email: teacherEmail,
      first_name: firstName,
      last_name: lastName,
      additional_data,
    })
  }

  archiveCustomerAccount() {
    return this._apiCore.patch("/archiveAccount");
  }

  activeCustomerAccount() {
    return this._apiCore.patch("/reActiveAccount");
  }

  getUserSafeDataById(id) {
    return this._apiCore.get(`/user/getsafedata/${id}`);
  }

  getETTeacher(campus) {
    // const address = `${campus.address_line1}, ${campus.suburb}, ${campus.state}, ${campus.postcode}`
    const address = campus.suburb?.toLowerCase();
    let state = campus.state;
    switch (campus.state.toUpperCase()) {
      case "VICTORIA":
        state = "VIC";
        break;
      case "QUEENSLAND":
        state = "QLD";
        break;
      case "AUSTRALIAN CAPITAL TERRITORY":
        state = "ACT";
        break;
      case "NEW SOUTH WALES":
        state = "NSW";
        break;
      case "NORTHERN TERRITORY":
        state = "NT";
        break;
      case "SOUTH AUSTRALIA":
        state = "SA";
        break;
      case "TASMANIA":
        state = "TA";
        break;
      case "WESTERN AUSTRALIA":
        state = "WA";
        break;
      default:
        state = campus.state;
        break;
    }
    return this._apiCore.get(`/morningET?state=${state}&address=${address}`);
  }

  getETTeacherV2(campus, type = null) {
    const address = campus.suburb?.toLowerCase();
    let state = campus.state;
    switch (campus.state.toUpperCase()) {
      case "VICTORIA":
        state = "VIC";
        break;
      case "QUEENSLAND":
        state = "QLD";
        break;
      case "AUSTRALIAN CAPITAL TERRITORY":
        state = "ACT";
        break;
      case "NEW SOUTH WALES":
        state = "NSW";
        break;
      case "NORTHERN TERRITORY":
        state = "NT";
        break;
      case "SOUTH AUSTRALIA":
        state = "SA";
        break;
      case "TASMANIA":
        state = "TA";
        break;
      case "WESTERN AUSTRALIA":
        state = "WA";
        break;
      default:
        state = campus.state;
        break;
    }
    return this._apiCore.get(`/V2/ET/morningET?state=${state}&address=${address}&type=${type}`);
  }

  sendAvailability(dates, times) {
    return this._apiCore.patch("/updateAvailability", {
      dates,
      times,
    });
  }

  sendAvailabilityByAdmin(dates, times, teacher_id) {
    return this._apiCore.patch("/updateAvailabilityByAdmin", {
      dates,
      times,
      teacher_id,
    });
  }

  sendTeacherProfileProgressEmail(data) {
    return this._apiCore.post("/sendTeacherProfileProgressEmail", data);
  }

  updateUserInfo(payload) {
    return this._apiCore.patch(`/updateUserInfo`, payload);
  }

  requestToViewCalendarV2(payload) {
    return this._apiCore.post(`/v2/requestViewCalendar/create-request-view`, payload);
  }

  updateRequestToViewCalendarV2(payload, id) {
    return this._apiCore.put(`/v2/requestViewCalendar/${id}/response-request-view`, payload);
  }

  updateUserInfoByAdmin(payload, userID) {
    return this._apiCore.patch(`/updateUserInfoByAdmin`, {
      ...payload,
      teacher_id: userID,
    });
  } 

  getQualificationList(usingCache = 'yes') {
    return this._apiCore.get(`/v2/customer/qualifications?usingCache=${usingCache}`)
  }

  searchTeacherWithFilter(query) {
    return this._apiCore.get('/v2/customer/teachers', {
      params: {...query}
    })
  }

  searchECECTeacherWithFilter(query) {
    return this._apiCore.get('/v2/customer/ECEC-teachers', {
      params: {...query}
    })
  }

  bookingReport(start,end) {
    return this._apiCore.get(`/V2/dashboard/bookingReport?start=${start}&end=${end}`)
  } 
  bookingStat(start,end) {
    return this._apiCore.get(`/V2/dashboard/booking?start=${start}&end=${end}`)
  } 
  bookingStateReport() {
    return this._apiCore.get(`/V2/dashboard/bookingState`)
  } 
  teacherReport() {
    return this._apiCore.get(`/V2/dashboard/user`)
  } 
  morningETReport(start,end) {
    return this._apiCore.get(`/V2/dashboard/etReport?start=${start}&end=${end}`)
  } 
  unassignedBook() {
    return this._apiCore.get(`/V2/ET/bookCount`)
  } 
  avaTeacher() {
    return this._apiCore.get(`/V2/ET/teachCount`)
  } 
  topTen(start,end) {
    return this._apiCore.get(`/V2/dashboard/topTen?start=${start}&end=${end}`)
  } 
  allocateET() {
    return this._apiCore.get(`/V2/ET/allocateET`)
  }
  morningET() {
    return this._apiCore.post(`/V2/ET/morningET`)
  }
  newReg() {
    return this._apiCore.get(`/V2/dashboard/newReg`)
  } 

  updateFeedback(payload) {
    return this._apiCore.put(`/v2/feedback`, payload)
  }

  updateCanDeliverOfInstitution(payload) {
    return this._apiCore.put('/v2/institution/can_delivers', payload)
  }

  updateRoomsProgramsOfInstitution(payload) {
    return this._apiCore.put('/v2/institution/rooms_programs', payload)
  }
  
  allocateAI(id) {
    return this._apiCore.get(`/V2/ET/allocateAI?booking=${id}`)
  }

  assignAI(payload) {
    return this._apiCore.post(`/V2/ET/assignAi`,payload)
  }
  
  etUpdate(payload) {
    return this._apiCore.post(`/V2/ET/etStatus`,payload)
  }
  getetstat(id) {
    return this._apiCore.get(`/V2/ET/etStatus?id=${id}`)
  }

  getSimpleQualification() {
    return this._apiCore.get('/v2/customer/simpleQualifications')
  }

  allTeachersSearchableForAllocate() {
    return this._apiCore.get("/v2/allocate/allTeachersSearchable");
  }

  getCountries() {
    return this._apiCore.get(`countries`);
  }

  uploadFileToS3(payload) {
    return this._apiCore.post(`/s3`, payload);
  }

  updateReference(payload) {
    return this._apiCore.put("/reference/submit", payload);
  }

  updateShareCollegeProfile(payload) {
    return this._apiCore.put("/shareCollegeProfile", payload);
  }

  updateShareUserProfile(payload) {
    return this._apiCore.put("/shareUserProfile", payload);
  }

  requestOTP(params) {
    return this._apiCore.get("/requestOTP", params);
  }

  verifyOTP(params) {
    return this._apiCore.get("/verifyOTP", params);
  }

  confirmUser(id) {
    return this._apiCore.get(`/user/confirm/${id}`);
  }

  register(payload) {
    return this._apiCore.post(`/register`, payload);
  }

  googleRegistration(payload) {
    return this._apiCore.post(`/googleRegistration/${payload.email}`, payload);
  }

  registerCustomer(payload) {
    return this._apiCore.post(`/customer/register`, payload);
  }

  loginGoogleCustomer(payload) {
    return this._apiCore.post(`customer/logingoogle`, payload);
  }

  registerGoogleCustomer(payload) {
    return this._apiCore.post(`/customer/registergoogle`, payload);
  }

  confirmCustomer(id) {
    return this._apiCore.get(`/customer/confirm/${id}`);
  }

  confirmPersonnel(id) {
    return this._apiCore.get(`/personnel/confirm/${id}`);
  }

  getEmailByToken(tokenId) {
    return this._apiCore.get(`/getemail/${tokenId}`);
  }

  resetPassword(payload) {
    return this._apiCore.put(`/ResetPassword`, payload);
  }

  requestPasswordReset(payload) {
    return this._apiCore.post(`/requestPasswordReset`, payload);
  }

  sendReferenceCheck(payload) {
    return this._apiCore.put(`/sendReferenceCheck`, payload);
  }

  personnelVerifyOTP(payload) {
    return this._apiCore.post("/personnel/verifyOTP", payload);
  }

  logErrorToSlack(payload) {
    return this._apiCore.post(`/V2/error-log-slack`, payload);
  }

  checkValidRequestViewCalendarV2(teacher_id) {
    return this._apiCore.get(
      `/v2/requestViewCalendar/check-valid-request-v2?teacher_id=${teacher_id}`
    );
  }

  acceptContract(payload) {
    return this._apiCore.post(`/v2/customer/accept-contract`, payload);
  }

  getContractUrlWithoutAccepting() {
    return this._apiCore.get(`/v2/customer/get-contract-url`);
  }

  getTotalHoursOfTeacher(teacher_id) {
    return this._apiCore.get(
      `/v2/user/total-hours-by-teacher/${teacher_id}`
    );
  }

  getBookingListWithFilterAndPagination(instID, filter, {limit = 20, page = 1}, bookingID) {
    return this._apiCore.get(
      `/v2/bookings/getBookingListWithFilterAndPagination/${instID}?limit=${limit}&page=${page}`,
      {
        params: {...filter, booking_id: bookingID}
      }
    )
  }

  getBookingListWithFilterAndPaginationV2(instID, filter, {limit = 20, page = 1}, bookingID) {
    return this._apiCore.get(
      `/v2/bookings/getBookingListWithFilterAndPaginationV2/${instID}?limit=${limit}&page=${page}`,
      {
        params: {...filter, booking_id: bookingID}
      }
    )
  }

  adminRequestViewCalendarV2(payload) {
    return this._apiCore.post(
      `/v2/requestViewCalendar/admin-request-view`, payload
    );
  }

  getListNotifications(limit, page, type = "") {
    return this._apiCore.get(
      `/v2/notification/inst-notification?limit=${limit}&page=${page}&type=${type}`
    );
  }

  getListTeacherNotifications(limit, page, type = "") {
    return this._apiCore.get(
      `/v2/notification/teacher-notification?limit=${limit}&page=${page}&type=${type}`
    );
  }

  readNotification(id) {
    return this._apiCore.post(`/v2/notification/read-notification/${id}`);
  }

  readAllNotification(payload) {
    return this._apiCore.post(`/v2/notification/read-all-notification`, payload);
  }

  managerReport(start,end) {
    return this._apiCore.get(
      `/V2/ET/managerReport?start=${start}&end=${end}`
    );
  }
  eventsCalendar() {
    return this._apiCore.get(
      `/V2/dashboard/eventsCalendar`
    );
  }
  newEventsCalendar(payload) {
    return this._apiCore.post(
      `/V2/dashboard/eventsCalendar`, payload
    );
  }
  delEventsCalendar(payload) {
    return this._apiCore.delete(
      `/V2/dashboard/eventsCalendar`,{data: payload}
    );
  }

  markAsUnPaidInvoices(invoice_ids) {
    return this._apiCore.post("/v2/invoice/revert-status", {
      invoice_ids
    });
  }

  countBookingForClassList(classes) {
    return this._apiCore.get(
      `/V2/bookings/countBookingForClasses?classes=${encodeURIComponent(JSON.stringify(classes))}`
    )
  }

  listApproversInAdmin() {
    return this._apiCore.get('/v2/invoice/approvers/list');
  }

  listCampusesByInstId(instId) {
    return this._apiCore.get(`/v2/campus/institution/${instId}`);
  }

  getInstitutionById(instId) {
    return this._apiCore.get(`/v2/customer/finance-settings/${instId}`);
  }

  updateQualification(ID, { hourly_rate }) {
    return this._apiCore.patch(`/v2/customer/qualifications/${ID}`, {
      hourly_rate
    })
  }

  createPersonnelV2(data) {
    return this._apiCore.post("/v2/personnel/createPersonnel", data);
  }

  updateVetLevelTeacher(teacher_id, vet_level) {
    return this._apiCore.put('/v2/user/vet-level', {
      teacher_id,
      vet_level
    });
  }

  getPersonnelByInstIdV2(inst_id) {
    return this._apiCore.get(`/V2/personnel/getPersonnelsByInstID`, {
      params: { inst_id: inst_id },
    });
  }

  getPayrollTax() {
    return this._apiCore.get(`/V2/invoice/payroll-tax/list`);
  }

  updatePayrollTax(id, data) {
    return this._apiCore.put(`/V2/invoice/payroll-tax/${id}`, {
      data
    });
  }

  fetchCustomersPagination(page, limit) {
    return this._apiCore.get(`/V2/customer`);
  }

  updateInvoiceSetting(id, settingData) {
    return this._apiCore.put(`/V2/customer/invoice-setting/${id}`, {
      settingData
    });
  }
  getAuthUrlGoogleDrive() {
    return this._apiCore.get(`/V2/google-drive/get-auth-url`)
  }

  getTokenGoogleDrive(code) {
    return this._apiCore.get(`/V2/google-drive/get-token?code=${code}`)
  }

  uploadGoogleDriveFileToS3(payload) {
    return this._apiCore.post(`/V2/google-drive/upload-file-to-s3`, payload)
  }

  readTextFromFileDragDrop(payload) {
    return this._apiCore.post(`/V2/qualification/read-text-pdf`,
      payload,
      {
        "Content-Type": "multipart/form-data",
      }
    )
  }

  readTextFromGoogleDriveFile(payload) {
    return this._apiCore.post(`/V2/qualification/read-text-drive-pdf`, payload)
  }

  readQualificationContentFromText(payload) {
    return this._apiCore.post(`/V2/ML/readQualification`, payload)
  }

  updateFromLinkedin(payload){
    return this._apiCore.post('/v2/user/linkedinCallback', payload);
  }

  updateFromFB(payload){
    return this._apiCore.post('/v2/user/facebookCallback', payload);
  }

  extractData(payload){
    return this._apiCore.post('/V2/ML/readQualification', payload);
  }

  getCountriesV2(){
    return this._apiCore.get('/v2/user/countryList');
  }

  uploadCompressedFileToS3(payload){
    return this._apiCore.post('/v2/user/upload-compressed-file-to-s3',
      payload,
      {
        "Content-Type": "multipart/form-data",
      }
    );
  }

  getLogSearchableByUserId(userId) {
    return this._apiCore.get(`/v2/logs/is-searchable-teacher/${userId}`);
  }

  sendApproveInvoiceToFinance(personnel_ids, invoice_id, invoice_duo_date) {
    return this._apiCore.post('/v2/invoice/send-approve-invoice', {
      personnel_ids: personnel_ids,
      invoice_id: invoice_id,
      invoice_duo_date: invoice_duo_date,
    });
  }

  createInvoiceInXero(id) {
    return this._apiCore.post(`/v2/invoice/sync-invoice/${id}`);
  }

  invoiceLogsById(invoice_id) {
    return this._apiCore.get(`/v2/invoice/invoice-log/${invoice_id}`);
  }

  updateCustomPeriodSetting(institution_id, custom_period_setting) {
    return this._apiCore.put(`/v2/invoice/custom-period-settings/${institution_id}`,{
      custom_period_setting
    });
  }

  listCampusesHaveBooking() {
    return this._apiCore.get(`/v2/campus/booking-campuses`);
  }

  updateBudgetInstitution(institution_id, budget_data) {
    return this._apiCore.put(`/v2/customer/update-budget/${institution_id}`,{
      budget_data
    });
  }
  acceptOrRejectDocument(payload) {
    return this._apiCore.post('/V2/validate/acceptOrRejectDocument', payload);
  }

  allUsersPayLevel(page, limit, keyword, filter = {}) {
    const keywordParam = keyword ? `&keyword=${keyword}` : "";
    const query = {
      page,
      limit,
      ...filter,
    };
    return this._apiCore.get(`v2/user/pay-level`, {
      params: { ...query },
    });
  }

  getHolidayList(state, year) {
    return this._apiCore.get(`/v2/holidays?state=${state}&year=${year}`);
  }

  approveInvoiceItemsInAdmin(invoice_id, payload) {
    return this._apiCore.post(`/v2/invoice/send-email-to-customer-for-revision/${invoice_id}`);
  }

  getBookingWithSameDateAndLevel(start_date, end_date, institution_id, teacher_id, class_id, booking_id) {
    return this._apiCore.get(`/v2/bookings/get-booking-with-same-date-and-level`, {
      params: {
        start_date,
        end_date,
        institution_id,
        teacher_id,
        class_id,
        booking_id
      }
    });
  }

}

export const API = new coreAPI();
